/**
 * <carousel-element> — generic controller for CSS scroll-snap carousels.
 *
 * Replaces the live theme's Swiper instances (hero mobile slider, logo bar,
 * testimonials, blog posts) with one tiny vanilla module.
 *
 * Expected markup:
 *   <carousel-element data-autoplay="0">
 *     <div class="carousel__track" data-carousel-track> ...slides... </div>
 *     <button data-carousel-prev></button>  (optional)
 *     <button data-carousel-next></button>  (optional)
 *     <div class="carousel__dots" data-carousel-dots data-dot-label="Go to slide"></div> (optional)
 *   </carousel-element>
 *
 * - data-autoplay: seconds between slides; 0/absent = no autoplay (live uses 0).
 * - Autoplay is disabled for prefers-reduced-motion users and paused on
 *   hover, focus, touch and when the tab is hidden.
 */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class CarouselElement extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;
    this.slides = Array.from(this.track.children);
    if (this.slides.length < 2) return;

    this.index = 0;

    const prev = this.querySelector('[data-carousel-prev]');
    const next = this.querySelector('[data-carousel-next]');
    if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
    if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

    this.buildDots();
    this.observeActiveSlide();
    this.setupAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.observer) this.observer.disconnect();
  }

  goTo(rawIndex) {
    const count = this.slides.length;
    const index = ((rawIndex % count) + count) % count;
    const offset = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
    this.track.scrollTo({ left: offset, behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth' });
  }

  buildDots() {
    this.dotsWrap = this.querySelector('[data-carousel-dots]');
    if (!this.dotsWrap) return;
    const label = this.dotsWrap.dataset.dotLabel || 'Slide';
    this.dots = this.slides.map((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'carousel__dot';
      dot.setAttribute('aria-label', `${label} ${i + 1}`);
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsWrap.appendChild(dot);
      return dot;
    });
    this.syncDots();
  }

  observeActiveSlide() {
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.index = this.slides.indexOf(entry.target);
            this.syncDots();
          }
        }
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  syncDots() {
    if (!this.dots) return;
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.index);
      if (i === this.index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  setupAutoplay() {
    const seconds = parseFloat(this.dataset.autoplay || '0');
    if (!(seconds > 0) || REDUCED_MOTION.matches) return;

    const start = () => {
      this.stopAutoplay();
      this.timer = setInterval(() => this.goTo(this.index + 1), seconds * 1000);
    };

    start();
    this.addEventListener('pointerenter', () => this.stopAutoplay());
    this.addEventListener('pointerleave', start);
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', start);
    this.addEventListener('touchstart', () => this.stopAutoplay(), { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stopAutoplay();
      else start();
    });
  }

  stopAutoplay() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('carousel-element')) {
  customElements.define('carousel-element', CarouselElement);
}
